import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Instagram, Loader2, Check } from "lucide-react";
import { useSettings } from "../hooks/useSettings";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const reveal = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export const Contact = () => {
  const STUDIO = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill in name, email and message.");
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll be in touch soon!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send your message. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="relative bg-black py-24 sm:py-32 border-t border-neutral-900">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <p className="text-xs uppercase tracking-[0.35em] text-neon mb-5">Contact</p>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mb-10">
            Come <span className="text-stroke-neon">sweat</span> with us
          </h2>

          <div className="flex flex-col gap-6">
            <a href={`https://maps.google.com/?q=${encodeURIComponent(STUDIO.address)}`} target="_blank" rel="noreferrer"
               data-testid="contact-address" className="flex items-start gap-4 group">
              <MapPin size={20} className="text-neon shrink-0 mt-1" />
              <span className="text-lg text-neutral-300 group-hover:text-white transition-colors">{STUDIO.address}</span>
            </a>
            <a href={`tel:${(STUDIO.phone || "").replace(/[^0-9+]/g, "")}`} id="contact-phone" data-testid="contact-phone" className="flex items-center gap-4 group">
              <Phone size={20} className="text-neon shrink-0" />
              <span className="text-lg text-neutral-300 group-hover:text-white transition-colors">{STUDIO.phone}</span>
            </a>
            <a href={`mailto:${STUDIO.email}`} id="contact-email" data-testid="contact-email" className="flex items-center gap-4 group">
              <Mail size={20} className="text-neon shrink-0" />
              <span className="text-lg text-neutral-300 group-hover:text-white transition-colors break-all">{STUDIO.email}</span>
            </a>
            <div className="flex items-center gap-4">
              <Instagram size={20} className="text-neon shrink-0" />
              <span className="text-lg text-neutral-300">{STUDIO.instagram}</span>
            </div>
          </div>
        </motion.div>

        <motion.form
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          onSubmit={submit}
          data-testid="contact-form"
          className="bg-[#0A0A0A] border border-neutral-800 p-8 flex flex-col gap-5"
        >
          <div>
            <Label htmlFor="contact-name" className="text-xs text-neutral-400">Name</Label>
            <Input id="contact-name" data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-black border-neutral-800 mt-1" placeholder="Your name" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact-email" className="text-xs text-neutral-400">Email</Label>
              <Input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-black border-neutral-800 mt-1" placeholder="you@email.com" />
            </div>
            <div>
              <Label htmlFor="contact-phone" className="text-xs text-neutral-400">Phone</Label>
              <Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-black border-neutral-800 mt-1" placeholder="Optional" />
            </div>
          </div>
          <div>
            <Label htmlFor="contact-message" className="text-xs text-neutral-400">Message</Label>
            <Textarea id="contact-message" data-testid="contact-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="bg-black border-neutral-800 mt-1 resize-none" placeholder="Tell us what you're after…" />
          </div>
          <button
            type="submit"
            disabled={loading || sent}
            data-testid="contact-submit"
            className="inline-flex items-center justify-center gap-2 bg-neon text-black font-semibold text-xs uppercase tracking-[0.2em] py-4 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : sent ? <Check size={16} /> : null}
            {sent ? "Message Sent" : loading ? "Sending…" : "Send Message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
